import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, Globe, Trophy, Users } from "lucide-react";

const benefits = [
  {
    icon: GraduationCap,
    title: "Pathway to Oxbridge & Ivy League",
    description:
      "Elite boarding schools provide rigorous preparation and direct pathways to the world's top universities — Oxford, Cambridge, Harvard, Stanford, and beyond.",
  },
  {
    icon: Globe,
    title: "A Global Network for Life",
    description:
      "Students build lasting friendships with peers from across the world — future leaders, entrepreneurs, and changemakers who become lifelong connections.",
  },
  {
    icon: Trophy,
    title: "Excellence in Music, Sport & Arts",
    description:
      "World-class facilities and coaching enable students to pursue passions in music, sport, drama, and art — while maintaining the highest academic standards.",
  },
  {
    icon: Users,
    title: "Character & Independence",
    description:
      "Boarding develops resilience, self-discipline, and emotional intelligence — qualities that distinguish students throughout their lives and careers.",
  },
];

const Problem = () => {
  return (
    <section id="problem" className="py-16 md:py-20 lg:py-28 bg-[#F5F5F0]">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
        >
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-semibold text-[#C9A227] mb-4">
            Why Elite Boarding
          </p>
          <h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-medium text-[#1A202C] tracking-tight mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Opening Doors to{" "}
            <span className="text-[#2B4C7E]">Extraordinary Futures</span>
          </h2>
          <p className="text-lg text-[#4A5568] leading-relaxed">
            The right boarding school is more than education — it's a launchpad for
            academic excellence, global connections, and personal mastery.
          </p>
        </motion.div>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 max-w-5xl mx-auto">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="bg-white p-5 sm:p-6 md:p-8 rounded-[4px] border border-[#E2E8F0] hover:border-[#C9A227] hover:shadow-lg transition-all duration-300"
                data-testid={`benefit-point-${index}`}
              >
                <div className="flex items-start gap-4 sm:gap-5">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-[#2B4C7E]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 sm:w-7 sm:h-7 text-[#2B4C7E]" />
                  </div>
                  <div>
                    <h3
                      className="text-lg sm:text-xl font-medium text-[#1A202C] mb-2"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {benefit.title}
                    </h3>
                    <p className="text-sm sm:text-base text-[#4A5568] leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Reassurance Statement */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 md:mt-16 max-w-3xl mx-auto p-6 sm:p-8 bg-white rounded-[4px] border-l-4 border-[#C9A227] shadow-lg"
        >
          <p
            className="text-lg sm:text-xl md:text-2xl text-[#2B4C7E] font-medium leading-relaxed text-center"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            "Finding the right school — where your child will thrive academically,
            socially, and personally — is the decision that shapes everything else."
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Problem;
